import { motion } from "framer-motion";
import { Eyebrow, GoldLine, Reveal, WhatsAppButton } from "@/components/bits";

/* ------------------------------- 1. HERO ------------------------------- */
export const Hero = () => (
  <section id="hero" data-testid="hero-section" className="relative overflow-hidden">
    <div className="pointer-events-none absolute -right-40 top-0 hidden h-[560px] w-[560px] bg-[#F4EFE6] lg:block" aria-hidden="true" />
    <div className="relative mx-auto grid max-w-7xl grid-cols-1 items-center gap-14 px-6 pb-20 pt-14 md:px-10 md:pt-20 lg:grid-cols-12 lg:gap-10 lg:pb-32 lg:pt-28">
      <div className="lg:col-span-7">
        <Reveal>
          <Eyebrow>Life Coaching Cristiano 1:1 per donne</Eyebrow>
        </Reveal>
        <Reveal delay={0.1}>
          <h1 className="mt-6 font-serif text-[2.6rem] leading-[1.05] tracking-tight text-[#1A1818] sm:text-6xl lg:text-7xl">
            Ti sei presa cura di tutti. Ma quando hai smesso di prenderti cura di te?
          </h1>
        </Reveal>
        <Reveal delay={0.2}>
          <p className="mt-8 max-w-xl text-base leading-relaxed text-[#3A3532] md:text-lg">
            Un percorso individuale di Life Coaching Cristiano con Chiara Perrone per lavorare
            sulla tua identità, ritrovare fiducia nelle tue scelte e costruire relazioni e
            abitudini più consapevoli. Con Cristo al centro.
          </p>
        </Reveal>
        <Reveal delay={0.3}>
          <div className="mt-8 max-w-xl space-y-1 border-l-2 border-[#D4AF37] pl-6 text-base leading-relaxed text-[#3A3532] md:text-lg">
            <p>Non devi diventare perfetta.</p>
            <p>
              Devi imparare a diventare una donna più consapevole, responsabile, radicata e capace
              di custodire ciò che Dio ti ha affidato.
            </p>
          </div>
        </Reveal>
        <Reveal delay={0.4}>
          <div className="mt-10">
            <WhatsAppButton label="Parla con Chiara" testid="hero-whatsapp-cta" />
            <p className="mt-4 text-sm text-[#3A3532]/80">
              Scrivimi direttamente e raccontami in poche parole dove sei oggi.
            </p>
            <p className="mt-6 text-xs font-medium uppercase tracking-[0.22em] text-[#3A3532]/60">
              Percorso individuale • Life Coaching Cristiano • Online
            </p>
          </div>
        </Reveal>
      </div>
      <div className="lg:col-span-5">
        <motion.div
          initial={{ opacity: 0, y: 32 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1.1, delay: 0.25, ease: [0.22, 1, 0.36, 1] }}
          className="relative mx-auto max-w-md lg:max-w-none"
        >
          <div className="absolute -left-4 -top-4 h-full w-full border border-[#D4AF37]/50" aria-hidden="true" />
          <div className="relative overflow-hidden">
            <img
              src="/images/chiara-hero.jpg"
              alt="Chiara Perrone, Life Coach Cristiana e founder di Donna Virtuosa"
              data-testid="hero-image"
              className="h-full w-full scale-100 object-cover transition-transform duration-[2000ms] ease-out hover:scale-105"
              loading="eager"
            />
          </div>
          <p className="mt-4 text-right text-xs uppercase tracking-[0.22em] text-[#3A3532]/50">
            Chiara Perrone — Life Coach Cristiana
          </p>
        </motion.div>
      </div>
    </div>
    <div className="relative mx-auto max-w-5xl px-6 pb-20 md:px-10 md:pb-28">
      <Reveal>
        <div
          data-testid="hero-video-slot"
          className="overflow-hidden border border-[#D4AF37]/50 bg-[#1A1818]"
        >
          <div className="aspect-video">
            <iframe
              src="https://www.youtube-nocookie.com/embed/hlAFnaWEeow?rel=0"
              title="Video di presentazione di Donna Virtuosa — Chiara Perrone"
              data-testid="hero-video-iframe"
              className="h-full w-full"
              loading="lazy"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          </div>
        </div>
      </Reveal>
    </div>
  </section>
);

/* ------------------------------- 2. PAIN ------------------------------- */
const painLines = [
  "Forse sei diventata quella che tiene tutto insieme.",
  "La famiglia.",
  "Il lavoro.",
  "La relazione.",
  "I figli.",
  "Le responsabilità.",
  "Le aspettative.",
  "I problemi degli altri.",
];

const painInner = [
  "Relazioni e difficoltà di comunicazione.",
  "Autostima e paura del giudizio.",
  "Confini personali e difficoltà a dire di no.",
  "Consapevolezza emotiva.",
  "Identità e direzione personale.",
  "Fede cristiana applicata alle scelte quotidiane.",
];

export const Pain = () => (
  <section data-testid="pain-section" className="bg-[#F4EFE6]">
    <div className="mx-auto max-w-3xl px-6 py-24 md:px-10 md:py-36">
      <Reveal>
        <h2 className="font-serif text-3xl leading-tight tracking-tight text-[#1A1818] md:text-5xl">
          Forse hai imparato a essere forte.
          <span className="mt-3 block text-2xl text-[#3A3532] md:text-3xl">
            Ma nessuno ti ha insegnato a custodire il tuo cuore.
          </span>
        </h2>
      </Reveal>
      <Reveal delay={0.1}>
        <GoldLine className="mt-10" />
      </Reveal>
      <div className="mt-10 space-y-2 text-base leading-relaxed text-[#3A3532] md:text-lg">
        {painLines.map((line, i) => (
          <Reveal key={line} delay={0.05 * i}>
            <p className={i === 0 ? "font-medium text-[#1A1818]" : ""}>{line}</p>
          </Reveal>
        ))}
      </div>
      <Reveal delay={0.15}>
        <p className="mt-10 text-base leading-relaxed text-[#3A3532] md:text-lg">
          E mentre fuori cerchi di essere presente per tutti, dentro di te possono esserci ancora
          parti che chiedono attenzione.
        </p>
      </Reveal>
      <div className="mt-8 grid grid-cols-1 gap-x-10 sm:grid-cols-2">
        {painInner.map((line, i) => (
          <Reveal key={line} delay={0.04 * i}>
            <p className="flex items-baseline gap-3 border-b border-[#2A2421]/10 py-3 text-base text-[#3A3532]">
              <span className="h-px w-4 shrink-0 translate-y-[-4px] bg-[#D4AF37]" aria-hidden="true" />
              {line}
            </p>
          </Reveal>
        ))}
      </div>
      <Reveal delay={0.1}>
        <p className="mt-10 text-base leading-relaxed text-[#3A3532] md:text-lg">
          E a volte anche il tuo rapporto con Dio diventa qualcosa che vivi soltanto quando stai
          male.
        </p>
      </Reveal>
      <Reveal delay={0.15}>
        <div className="mt-12 space-y-2">
          <p className="font-serif text-2xl leading-snug text-[#1A1818] md:text-3xl">
            Donna Virtuosa nasce per lavorare più in profondità.
          </p>
          <p className="text-base text-[#3A3532] md:text-lg">Non soltanto su ciò che fai.</p>
          <p className="font-serif text-xl italic text-[#1A1818] md:text-2xl">
            Ma sulla donna da cui quelle azioni nascono.
          </p>
        </div>
      </Reveal>
    </div>
  </section>
);

/* ----------------------------- 3. MANIFESTO ---------------------------- */
const manifestoLines = [
  "Puoi essere sensibile senza diventare schiava delle tue emozioni.",
  "Puoi amare senza annullarti.",
  "Puoi mettere confini senza sentirti cattiva.",
  "Puoi prenderti cura degli altri senza dimenticare te stessa.",
  "Puoi avere una voce senza perdere la tua femminilità.",
  "Puoi diventare una donna forte e, allo stesso tempo, completamente radicata in Dio.",
];

export const Manifesto = () => (
  <section data-testid="manifesto-section" className="bg-[#1A1818] text-[#FDFBF7]">
    <div className="mx-auto max-w-4xl px-6 py-24 md:px-10 md:py-36">
      <Reveal>
        <h2 className="font-serif text-3xl leading-tight tracking-tight sm:text-4xl md:text-6xl">
          Puoi essere forte senza indurire il tuo cuore.
        </h2>
      </Reveal>
      <Reveal delay={0.15}>
        <GoldLine className="mt-10" />
      </Reveal>
      <div className="mt-12 space-y-0">
        {manifestoLines.map((line, i) => (
          <Reveal key={line} delay={0.06 * i}>
            <p className="border-b border-[#FDFBF7]/10 py-5 font-serif text-xl leading-snug text-[#FDFBF7]/85 md:text-2xl">
              {line}
            </p>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

/* ---------------------------- 4. FERITA DI EVA --------------------------- */
const evaDynamics = [
  "La paura.",
  "La vergogna.",
  "Il nascondimento.",
  "Il bisogno di controllare.",
  "La difficoltà a fidarsi.",
  "Il tentativo di trovare valore e sicurezza lontano dalla propria identità in Dio.",
];

export const Eva = () => (
  <section data-testid="eva-section">
    <div className="mx-auto grid max-w-7xl grid-cols-1 gap-14 px-6 py-24 md:px-10 md:py-36 lg:grid-cols-12">
      <div className="lg:col-span-5">
        <div className="lg:sticky lg:top-32">
          <Reveal>
            <Eyebrow>Da dove inizia tutto</Eyebrow>
          </Reveal>
          <Reveal delay={0.1}>
            <h2 className="mt-6 font-serif text-3xl leading-tight tracking-tight text-[#1A1818] md:text-5xl">
              La ferita di Eva non è rimasta nel giardino.
            </h2>
          </Reveal>
          <Reveal delay={0.2}>
            <GoldLine className="mt-10" />
          </Reveal>
        </div>
      </div>
      <div className="lg:col-span-7">
        <Reveal>
          <p className="text-base leading-relaxed text-[#3A3532] md:text-lg">
            Quando leggiamo Genesi non troviamo soltanto la storia di una donna vissuta migliaia di
            anni fa.
          </p>
        </Reveal>
        <Reveal delay={0.05}>
          <p className="mt-6 text-base leading-relaxed text-[#3A3532] md:text-lg">
            Troviamo dinamiche che ancora oggi possiamo riconoscere dentro di noi.
          </p>
        </Reveal>
        <div className="mt-8">
          {evaDynamics.map((line, i) => (
            <Reveal key={line} delay={0.05 * i}>
              <p className="flex items-baseline gap-3 border-b border-[#2A2421]/10 py-3 text-base text-[#3A3532] md:text-lg">
                <span className="h-px w-4 shrink-0 translate-y-[-5px] bg-[#D4AF37]" aria-hidden="true" />
                {line}
              </p>
            </Reveal>
          ))}
        </div>
        <Reveal delay={0.1}>
          <p className="mt-10 font-medium text-[#1A1818] md:text-lg">Donna Virtuosa parte anche da qui.</p>
        </Reveal>
        <Reveal delay={0.12}>
          <div className="mt-6 space-y-2 text-base text-[#3A3532] md:text-lg">
            <p>Non per condannare Eva.</p>
            <p>Non per colpevolizzare la donna.</p>
            <p>E non per costruire la caricatura della “brava donna cristiana”.</p>
          </div>
        </Reveal>
        <Reveal delay={0.15}>
          <p className="mt-6 text-base leading-relaxed text-[#3A3532] md:text-lg">
            Ma per imparare a riconoscere quelle dinamiche interiori che ancora oggi possono
            condizionare il modo in cui una donna ama, reagisce, comunica, si relaziona e prende
            decisioni.
          </p>
        </Reveal>
        <Reveal delay={0.2}>
          <blockquote className="mt-12 border-l-2 border-[#D4AF37] bg-[#F4EFE6] p-8 md:p-10" data-testid="eva-quote">
            <p className="font-serif text-2xl leading-snug text-[#1A1818] md:text-3xl">
              Non puoi cambiare ciò che è successo ieri.
            </p>
            <p className="mt-3 font-serif text-xl italic leading-snug text-[#3A3532] md:text-2xl">
              Ma puoi smettere di permettere al passato di governare ciò che scegli oggi.
            </p>
          </blockquote>
        </Reveal>
      </div>
    </div>
  </section>
);
